// src/components/settings/FeedbackSection.jsx
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  useSubmitFeedbackMutation,
  useGetMyFeedbackQuery,
} from '../../store/api/feedbackApi';
import SettingsCard from './ui/SettingsCard';
import s from './section.module.css';
import f from './FeedbackSection.module.css';

const CATEGORIES = [
  { value: 'Bug',             label: '🐛 Bug Report',        desc: 'Something is broken or not working' },
  { value: 'Feature Request', label: '💡 Feature Request',   desc: 'Suggest a new feature or improvement' },
  { value: 'UI/UX',          label: '🎨 UI / UX',            desc: 'Design, layout or usability feedback' },
  { value: 'Performance',    label: '⚡ Performance',         desc: 'App is slow or laggy' },
  { value: 'General',        label: '💬 General Feedback',   desc: 'General thoughts about Nessefy' },
  { value: 'Other',          label: '📝 Other',              desc: 'Anything else' },
];

const STATUS_COLORS = {
  Pending:    { bg: '#fff8ec', color: '#b45309', dot: '#f59e0b' },
  Reviewed:   { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' },
  InProgress: { bg: '#f0fdf4', color: '#166534', dot: '#22c55e' },
  Resolved:   { bg: '#f0fdf4', color: '#166534', dot: '#16a34a' },
  Closed:     { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af' },
};

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: style.bg, color: style.color,
      padding: '2px 10px', borderRadius: 20,
      fontSize: '0.75rem', fontWeight: 700,
      fontFamily: "'Sora', sans-serif",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: style.dot, display: 'inline-block' }} />
      {status}
    </span>
  );
}

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className={f.stars}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className={`${f.star} ${(hovered || value) >= n ? f.starFilled : ''}`}
          onClick={() => onChange(value === n ? null : n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          aria-label={`${n} star`}
        >
          ★
        </button>
      ))}
      {value && (
        <span className={f.ratingLabel}>
          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value]}
        </span>
      )}
    </div>
  );
}

export default function FeedbackSection() {
  const [category, setCategory]   = useState('');
  const [rating, setRating]       = useState(null);
  const [message, setMessage]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState('submit'); // 'submit' | 'history'

  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const { data: myFeedback = [], isLoading: isLoadingHistory } = useGetMyFeedbackQuery(
    undefined,
    { skip: activeTab !== 'history' }
  );

  const handleSubmit = async () => {
    if (!category) { toast.error('Please select a category.'); return; }
    if (message.trim().length < 10) { toast.error('Please write at least 10 characters.'); return; }

    try {
      await submitFeedback({
        category,
        rating:     rating || undefined,
        message:    message.trim(),
        deviceInfo: `${navigator.userAgent.slice(0, 200)}`,
      }).unwrap();

      setSubmitted(true);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to submit. Please try again.');
    }
  };

  const handleReset = () => {
    setCategory('');
    setRating(null);
    setMessage('');
    setSubmitted(false);
  };

  return (
    <>
      {/* Tab switcher */}
      <div className={f.tabs}>
        <button
          className={`${f.tab} ${activeTab === 'submit' ? f.tabActive : ''}`}
          onClick={() => setActiveTab('submit')}
        >
          ✍️ Submit Feedback
        </button>
        <button
          className={`${f.tab} ${activeTab === 'history' ? f.tabActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📋 My Submissions
        </button>
      </div>

      {/* ── SUBMIT TAB ── */}
      {activeTab === 'submit' && (
        <SettingsCard
          title="Share Your Feedback"
          subtitle="Help us make Nessefy better. Every piece of feedback is read by our team."
        >
          {submitted ? (
            /* Success state */
            <div className={f.success}>
              <div className={f.successIcon}>🎉</div>
              <h3 className={f.successTitle}>Thank you!</h3>
              <p className={f.successText}>
                Your feedback has been submitted. We review every submission and
                will update the status in <strong>My Submissions</strong>.
              </p>
              <div className={s.btnRow}>
                <button className={s.btnPrimary} onClick={handleReset}>
                  Submit Another
                </button>
                <button className={s.btnOutline} onClick={() => { handleReset(); setActiveTab('history'); }}>
                  View My Submissions
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Category */}
              <div className={s.field}>
                <label className={s.label}>Category <span style={{ color: '#e05555' }}>*</span></label>
                <div className={f.categoryGrid}>
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      className={`${f.categoryBtn} ${category === c.value ? f.categoryBtnActive : ''}`}
                      onClick={() => setCategory(c.value)}
                    >
                      <span className={f.catLabel}>{c.label}</span>
                      <span className={f.catDesc}>{c.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div className={s.field}>
                <label className={s.label}>Overall Rating <span className={f.optional}>(optional)</span></label>
                <StarRating value={rating} onChange={setRating} />
              </div>

              {/* Message */}
              <div className={s.field}>
                <label className={s.label}>
                  Your Feedback <span style={{ color: '#e05555' }}>*</span>
                </label>
                <textarea
                  className={`${s.input} ${f.textarea}`}
                  placeholder={
                    category === 'Bug'
                      ? 'Describe the bug: what happened, what you expected, and how to reproduce it...'
                      : category === 'Feature Request'
                      ? 'Describe the feature you would like and why it would help you...'
                      : 'Share your thoughts, ideas or experience with Nessefy...'
                  }
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={2000}
                  rows={5}
                />
                <div className={f.charCount}>
                  <span style={{ color: message.length < 10 ? '#e05555' : '#8fa5b2' }}>
                    {message.length}/2000
                  </span>
                  {message.length < 10 && message.length > 0 && (
                    <span style={{ color: '#e05555', marginLeft: 8 }}>
                      {10 - message.length} more characters needed
                    </span>
                  )}
                </div>
              </div>

              <div className={s.btnRow}>
                <button
                  className={s.btnPrimary}
                  onClick={handleSubmit}
                  disabled={isLoading || !category || message.trim().length < 10}
                >
                  {isLoading ? 'Submitting...' : 'Submit Feedback'}
                </button>
                <button className={s.btnOutline} onClick={handleReset} disabled={isLoading}>
                  Clear
                </button>
              </div>
            </>
          )}
        </SettingsCard>
      )}

      {/* ── HISTORY TAB ── */}
      {activeTab === 'history' && (
        <SettingsCard
          title="My Submissions"
          subtitle="Track the status of feedback you have submitted."
        >
          {isLoadingHistory ? (
            <p className={f.empty}>Loading...</p>
          ) : myFeedback.length === 0 ? (
            <div className={f.empty}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📭</div>
              <p>You have not submitted any feedback yet.</p>
              <button className={s.btnOutline} style={{ marginTop: 12 }} onClick={() => setActiveTab('submit')}>
                Submit Your First Feedback
              </button>
            </div>
          ) : (
            <div className={f.feedbackList}>
              {myFeedback.map(item => (
                <div key={item.id} className={f.feedbackItem}>
                  <div className={f.feedbackHeader}>
                    <span className={f.feedbackCategory}>{item.category}</span>
                    <StatusBadge status={item.status} />
                    <span className={f.feedbackDate}>
                      {new Date(item.submittedAt).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                  </div>

                  {item.rating && (
                    <div className={f.feedbackRating}>
                      {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                    </div>
                  )}

                  <p className={f.feedbackMessage}>{item.message}</p>

                  {item.adminReply && (
                    <div className={f.adminReply}>
                      <div className={f.adminReplyHeader}>
                        <span>💬</span>
                        <strong>Nessefy Team replied</strong>
                        {item.repliedAt && (
                          <span className={f.replyDate}>
                            · {new Date(item.repliedAt).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short'
                            })}
                          </span>
                        )}
                      </div>
                      <p className={f.replyText}>{item.adminReply}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </SettingsCard>
      )}
    </>
  );
}